import React, { useCallback, useEffect, useState } from 'react';
import itiTypeSiteService from '../../services/itiTypeSiteService';
import itiFonctionnaliteService from '../../services/itiFonctionnaliteService';
import ItiTypeSiteFonctionnalitesModal from './ItiTypeSiteFonctionnalitesModal';
import ItiReferentielAdminPanel, { emptyCategoryForm } from './ItiReferentielAdminPanel';
import { AppButton } from '../ui';
import useAppConfirm from '../../hooks/useAppConfirm';
import { notify } from '../common/AppToast';

const emptyOptionForm = { libelle: '', sort_order: 0 };

const ItiTypesSiteAdminSection = () => {
  const { ask, modal } = useAppConfirm();
  const [categories, setCategories] = useState([]);
  const [fonctionnalitesGroups, setFonctionnalitesGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);
  const [optionForms, setOptionForms] = useState({});
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [fonctionnalitesModalOption, setFonctionnalitesModalOption] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [typesData, fonctionnalitesData] = await Promise.all([
        itiTypeSiteService.getAdminTree(),
        itiFonctionnaliteService.getAdminTree(),
      ]);
      setCategories(typesData);
      setFonctionnalitesGroups(fonctionnalitesData);
      setExpanded((prev) => {
        const next = { ...prev };
        typesData.forEach((cat) => {
          if (next[cat.id] === undefined) next[cat.id] = true;
        });
        return next;
      });
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreateCategory = async (event) => {
    event.preventDefault();
    if (!categoryForm.libelle.trim()) return;

    setSaving(true);
    try {
      await itiTypeSiteService.createCategory({
        libelle: categoryForm.libelle.trim(),
        icone: categoryForm.icone.trim() || null,
        sort_order: Number(categoryForm.sort_order) || 0,
      });
      setCategoryForm(emptyCategoryForm);
      setShowCategoryForm(false);
      notify.success('Catégorie ajoutée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCategory = async (category, patch) => {
    setSaving(true);
    try {
      await itiTypeSiteService.updateCategory(category.id, patch);
      notify.success(patch.active === false ? 'Catégorie masquée' : patch.active === true ? 'Catégorie visible' : 'Catégorie mise à jour');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (category) => {
    const ok = await ask({
      title: 'Supprimer cette catégorie ?',
      message: `Supprimer la catégorie « ${category.libelle} » et toutes ses options ?`,
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) {
      return;
    }

    setSaving(true);
    try {
      await itiTypeSiteService.deleteCategory(category.id);
      notify.success('Catégorie supprimée');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateOption = async (event, categoryId) => {
    event.preventDefault();
    const form = optionForms[categoryId] ?? emptyOptionForm;
    if (!form.libelle.trim()) return;

    setSaving(true);
    try {
      await itiTypeSiteService.createOption({
        categorie_id: categoryId,
        libelle: form.libelle.trim(),
        sort_order: Number(form.sort_order) || 0,
      });
      setOptionForms((prev) => ({ ...prev, [categoryId]: emptyOptionForm }));
      notify.success('Type de site ajouté');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateOption = async (option, patch) => {
    setSaving(true);
    try {
      await itiTypeSiteService.updateOption(option.id, patch);
      notify.success(patch.active === false ? 'Type masqué' : patch.active === true ? 'Type visible' : 'Type mis à jour');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOption = async (option) => {
    const ok = await ask({
      title: 'Supprimer ce type ?',
      message: `Supprimer « ${option.libelle} » ?`,
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
    });
    if (!ok) {
      return;
    }

    setSaving(true);
    try {
      await itiTypeSiteService.deleteOption(option.id);
      notify.success('Type supprimé');
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOptionFonctionnalites = async (optionId, fonctionnaliteIds) => {
    setSaving(true);
    try {
      await itiTypeSiteService.updateOptionFonctionnalites(optionId, fonctionnaliteIds);
      notify.success('Fonctionnalités liées mises à jour');
      setFonctionnalitesModalOption(null);
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const extraColumns = [
    {
      header: 'Fonctionnalités auto',
      render: (option) => (
        <div className="d-flex flex-wrap align-items-center gap-1">
          {(option.fonctionnalite_labels ?? []).slice(0, 2).map((label) => (
            <span key={label} className="badge iti-fonctionnalite-tag">{label}</span>
          ))}
          {(option.fonctionnalite_labels?.length ?? 0) > 2 && (
            <span className="badge iti-fonctionnalite-tag iti-fonctionnalite-tag--more">
              +{(option.fonctionnalite_labels?.length ?? 0) - 2}
            </span>
          )}
          {(option.fonctionnalite_labels?.length ?? 0) === 0 && (
            <span className="text-muted small">Aucune</span>
          )}
          <AppButton
            size="sm"
            variant="outline-primary"
            disabled={saving}
            onClick={() => setFonctionnalitesModalOption(option)}
            title="Lier des fonctionnalités automatiques"
            icon="mdi:link-variant"
          />
        </div>
      ),
    },
  ];

  return (
    <>
    <ItiReferentielAdminPanel
      addOptionLabel="Ajouter un type de site"
      emptyOptionMessage="Aucun type de site pour le moment. Utilisez le champ ci-dessus pour en ajouter un."
      optionCountSingular="type de site"
      optionCountPlural="types de site"
      optionPlaceholder="Ex. Site e-commerce"
      categoryPlaceholder="Ex. Types de site"
      categories={categories}
      loading={loading}
      saving={saving}
      expanded={expanded}
      onToggleCategory={(id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))}
      showCategoryForm={showCategoryForm}
      onToggleCategoryForm={() => setShowCategoryForm((prev) => !prev)}
      categoryForm={categoryForm}
      onCategoryFormChange={setCategoryForm}
      onCreateCategory={handleCreateCategory}
      optionForms={optionForms}
      onOptionFormChange={(categoryId, form) => setOptionForms((prev) => ({ ...prev, [categoryId]: form }))}
      onCreateOption={handleCreateOption}
      onUpdateCategory={handleUpdateCategory}
      onDeleteCategory={handleDeleteCategory}
      onUpdateOption={handleUpdateOption}
      onDeleteOption={handleDeleteOption}
      extraColumns={extraColumns}
      footerSlot={(
        <ItiTypeSiteFonctionnalitesModal
          open={Boolean(fonctionnalitesModalOption)}
          typeSiteOption={fonctionnalitesModalOption}
          fonctionnalitesGroups={fonctionnalitesGroups}
          saving={saving}
          onClose={() => setFonctionnalitesModalOption(null)}
          onSave={handleSaveOptionFonctionnalites}
        />
      )}
    />
    {modal}
    </>
  );
};

export default ItiTypesSiteAdminSection;
